import {
    Colors,
    LayerFlags,
    point3d,
    TextHorizontalAlignment,
    TextVerticalAlignment,
    Units,
    DxfWriter,
} from "@tarikjabiri/dxf";
import { drawBaseline2dShapes, drawEdge2dShapes, drawShapeToBlock } from "./DxfDraw";
import { calculateBlockDimensions } from "./DxfCalculations";
import { buildBaselineDimensionContext } from "./DxfBaselineDimensions";

const HOUSE_MODEL_LAYER = "House Modal";
const EDGE_DIMENSIONS_LAYER = "Edge Dimensions";
const BASELINE_DIMENSIONS_LAYER = "Edge Dimensions - Baseline";

// Main DXF generation function
export const generateDxf = (dxfData, projectName, options = {}) => {
    const { shouldDownload = true } = options;
    const dxf = new DxfWriter();
    const sectionGapX = 20 * 12;
    // Keep the edge heading clearly separated from the per-segment drawings.
    const sectionHeadingGap = 88;
    const sectionBreakGap = 250;
    const sectionBreakToCaptionGap = 200;
    const captionToRowGap = 220;
    const houseToEdgeDimensionsGap = 400;
    // All exported drawing coordinates are converted to inches.
    dxf.setUnits(Units.Inches);
    dxf.setVariable("$DIMALTU", { 70: 6 });
    dxf.setVariable("$DIMLUNIT", { 70: 4 });
    dxf.setVariable("$LUNITS", { 70: 4 });
    dxf.addLayer(HOUSE_MODEL_LAYER, Colors.White, undefined, LayerFlags.None);
    dxf.addLayer(EDGE_DIMENSIONS_LAYER, Colors.Cyan, undefined, LayerFlags.None);
    dxf.addLayer(BASELINE_DIMENSIONS_LAYER, Colors.Yellow, undefined, LayerFlags.None);

    // Keep the primary scene in model space. The writer uses only top-level
    // entities to calculate AutoCAD's initial viewport and cannot expand an
    // INSERT to its block geometry. Keeping the roof entities here ensures a
    // newly opened drawing frames the actual design instead of a blank origin.
    const sceneBlock = dxf.modelSpace;
    const baselineContext = buildBaselineDimensionContext(dxfData);
    sceneBlock.setLayerName(HOUSE_MODEL_LAYER);
    const edgeSections = [];

    // Draw all 3D model shapes into the scene block
    dxfData.forEach((item) => {
        // Draw main polygon with label
        drawShapeToBlock(dxf, item.polygon, sceneBlock, {
            addName: true,
            layerName: HOUSE_MODEL_LAYER,
        }, item.label);

        // Draw setback shapes
        let setbackShape = [];
        item.setbacks.forEach((sb) => {
            setbackShape.push(sb.vertex);
        });
        if (setbackShape.length > 0) {
            drawShapeToBlock(dxf, setbackShape, sceneBlock, { layerName: HOUSE_MODEL_LAYER });
        };

        // Draw obstructions
        item.obstruction.forEach((obs, obsIndex) => {
            drawShapeToBlock(dxf, obs.polygon, sceneBlock, { layerName: HOUSE_MODEL_LAYER }, obsIndex);
        });

        // Draw panels
        item.panels.forEach((panel, panIndex) => {
            drawShapeToBlock(dxf, panel.polygon, sceneBlock, { layerName: HOUSE_MODEL_LAYER }, panIndex);
        });
    });

    // Write layout entities directly to model space, avoiding nested BLOCK and
    // INSERT structures that can produce invalid AutoCAD extents/viewports.
    const calculatedSceneDimensions = calculateBlockDimensions(sceneBlock);
    const sceneDimensions =
        Number.isFinite(calculatedSceneDimensions.minX) &&
        Number.isFinite(calculatedSceneDimensions.minY) &&
        calculatedSceneDimensions.width >= 0 &&
        calculatedSceneDimensions.height >= 0
            ? calculatedSceneDimensions
            : { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 };
    const rowOriginX = sceneDimensions.minX;
    // Move the complete dimensions layout clear of the house model.
    const edgeRowY = sceneDimensions.minY - houseToEdgeDimensionsGap;
    let sectionStartX = 0;
    dxfData.forEach((item, index) => {
        const edgeContainer = createTranslatedContainer(
            dxf.modelSpace,
            rowOriginX + sectionStartX,
            edgeRowY,
            EDGE_DIMENSIONS_LAYER
        );
        const polyData = drawEdge2dShapes(dxf, item, edgeContainer, index, EDGE_DIMENSIONS_LAYER);
        edgeSections.push({
            bounds: shiftBounds(polyData, rowOriginX + sectionStartX, edgeRowY),
            insertX: rowOriginX + sectionStartX,
        });
        sectionStartX += (polyData.width || 0) + sectionGapX;
    });

    const edgeRowBounds = mergeBounds(edgeSections.map((section) => section.bounds));
    addSectionHeading(dxf.modelSpace, "Edge Dimensions", edgeRowBounds, EDGE_DIMENSIONS_LAYER, Colors.Cyan, sectionHeadingGap);

    const separatorY = (edgeRowBounds?.minY ?? edgeRowY) - sectionBreakGap;
    addSectionHeading(
        dxf.modelSpace,
        "Baseline Dimensions",
        edgeRowBounds ? { ...edgeRowBounds, maxY: separatorY } : null,
        BASELINE_DIMENSIONS_LAYER,
        Colors.Yellow,
        0
    );

    const baselineHeaderY = separatorY - sectionBreakToCaptionGap - captionToRowGap;
    edgeSections.forEach((section, index) => {
        const baselineContainer = createTranslatedContainer(
            dxf.modelSpace,
            section.insertX,
            baselineHeaderY,
            BASELINE_DIMENSIONS_LAYER
        );
        drawBaseline2dShapes(dxf, dxfData[index], baselineContainer, index, {
            highestEdges: baselineContext?.get(index) || [],
            layerName: BASELINE_DIMENSIONS_LAYER,
        });
    });

    // Output the final DXF to download
    return print(dxf, projectName, { shouldDownload });
};

// Utility function to generate and trigger DXF file download
function print(dxfInstance, projectName, options = {}) {
    const { shouldDownload = true } = options;
    const dxf_output = dxfInstance.stringify();

    const fileName = `${projectName || "design"}.dxf`;
    const dxfFile = new Blob([dxf_output], { type: "application/dxf" });

    if (shouldDownload) {
        const element = document.createElement("a");
        element.href = URL.createObjectURL(dxfFile);
        element.download = fileName;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        URL.revokeObjectURL(element.href);
    }

    return {
        fileName,
        blob: dxfFile,
    };
}

function shiftBounds(bounds, offsetX = 0, offsetY = 0) {
    return {
        ...bounds,
        minX: bounds.minX + offsetX,
        maxX: bounds.maxX + offsetX,
        minY: bounds.minY + offsetY,
        maxY: bounds.maxY + offsetY,
    };
}

function mergeBounds(boundsList) {
    const validBounds = boundsList.filter(
        (bounds) =>
            bounds &&
            Number.isFinite(bounds.minX) &&
            Number.isFinite(bounds.maxX) &&
            Number.isFinite(bounds.minY) &&
            Number.isFinite(bounds.maxY)
    );

    if (!validBounds.length) {
        return null;
    }

    return {
        minX: Math.min(...validBounds.map((bounds) => bounds.minX)),
        maxX: Math.max(...validBounds.map((bounds) => bounds.maxX)),
        minY: Math.min(...validBounds.map((bounds) => bounds.minY)),
        maxY: Math.max(...validBounds.map((bounds) => bounds.maxY)),
    };
}

function addSectionHeading(block, text, bounds, layerName, colorNumber, offsetY = 28) {
    if (!bounds) {
        return;
    }

    const position = point3d((bounds.minX + bounds.maxX) / 2, bounds.maxY + offsetY, 0.6);
    const heading = block.addText(position, 24, text, {
        rotation: 0,
        horizontalAlignment: TextHorizontalAlignment.Center,
        verticalAlignment: TextVerticalAlignment.Middle,
        colorNumber,
        secondAlignmentPoint: position,
    });
    heading.layerName = layerName;

    const overlineY = position.y + 16;
    const underlineY = position.y - 16;
    const overline = block.addLine(point3d(bounds.minX, overlineY, 0), point3d(bounds.maxX, overlineY, 0));
    overline.layerName = layerName;
    overline.colorNumber = colorNumber;
    const underline = block.addLine(point3d(bounds.minX, underlineY, 0), point3d(bounds.maxX, underlineY, 0));
    underline.layerName = layerName;
    underline.colorNumber = colorNumber;
}

function createTranslatedContainer(target, offsetX, offsetY, layerName) {
    const translatePoint = (point = {}) => point3d(
        (point.x || 0) + offsetX,
        (point.y || 0) + offsetY,
        point.z || 0
    );
    const applyLayer = (entity) => {
        entity.layerName = layerName;
        return entity;
    };

    return {
        setLayerName: () => {},
        addLine: (start, end) => applyLayer(target.addLine(translatePoint(start), translatePoint(end))),
        addCircle: (center, radius) => applyLayer(target.addCircle(translatePoint(center), radius)),
        addText: (point, height, text, options = {}) => applyLayer(target.addText(
            translatePoint(point),
            height,
            text,
            {
                ...options,
                secondAlignmentPoint: options.secondAlignmentPoint
                    ? translatePoint(options.secondAlignmentPoint)
                    : undefined,
            }
        )),
        addLWPolyline: (vertices, options = {}) => applyLayer(target.addLWPolyline(
            vertices.map((vertex) => ({
                ...vertex,
                point: {
                    x: (vertex.point?.x || 0) + offsetX,
                    y: (vertex.point?.y || 0) + offsetY,
                },
            })),
            options
        )),
    };
}
